import React, { useState, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { ScrollArea } from "../ui/scroll-area";
import { Separator } from "../ui/separator";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "../ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import { Textarea } from "../ui/textarea";
import { Plus, Briefcase, RefreshCw, AlertCircle } from "lucide-react";
import { API } from "../../App";
import { toast } from "sonner";
import { useTimezone } from "../../contexts/TimezoneContext";
import { usePlanLimits } from "../../contexts/PlanLimitContext";

import { getInitialFormData, isAutomotiveJob, getTechCommission, calculateTotals } from "../../utils/job-form-utils";
import { dialogContentOutsideGuards } from "../../utils/dialogOutsideGuards";
import { useAddressAutocomplete } from "./form/useAddressAutocomplete";
import { useJobSelections } from "./form/useJobSelections";
import { buildJobPayload } from "./form/buildJobPayload";
import JobDetailsSection from "./form/JobDetailsSection";
import VehicleSection from "./form/VehicleSection";
import CustomerSection from "./form/CustomerSection";
import AddressSection from "./form/AddressSection";
import ScheduleSection from "./form/ScheduleSection";
import JobDatesSection from "./JobDatesSection";
import CompletionSection from "./form/CompletionSection";
import JobTaxPanel from "./JobTaxPanel";
import RecurringJobPanel from "./RecurringJobPanel";
import CustomerAddressPicker from "./CustomerAddressPicker";
import CustomerModal from "../customers/CustomerModal";
import useJobStatuses from "../../hooks/useJobStatuses";

const FALLBACK_STATUS_OPTIONS = [
  { id: "estimate", label: "Estimate" }, { id: "scheduled", label: "Scheduled" },
  { id: "in_progress", label: "In Progress" }, { id: "deposit_collected", label: "Deposit Collected" },
  { id: "completed", label: "Completed" }, { id: "completed_pending_payment", label: "Pending Payment" },
  { id: "cancelled", label: "Cancelled" },
];
import { splitLegacyAddress } from "./form/legacyAddress";
import JobAttachments, { PendingAttachmentsPicker, uploadJobAttachments } from "./JobAttachments";

const getAuthConfig = () => {
  const token = localStorage.getItem("token");
  return { headers: token ? { Authorization: `Bearer ${token}` } : {} };
};

const JobFormDialog = ({
  open, onOpenChange, editingJob, ticketPrefill, customerPrefill, schedulePrefill, selectedDate,
  customers, teamMembers, jobTypes, jobSources, paymentTypes, vehicleData,
  loading, onSuccess, onRefresh, variant = "dialog",
}) => {
  const isPage = variant === "page";
  const { getCurrentTime, getToday } = useTimezone();
  const { isSmsReady, hasActivePhone } = usePlanLimits();
  const { assignable: statusOptions, getStatus } = useJobStatuses();
  const navigate = useNavigate();

  // Core form state
  const [formData, setFormData] = useState(getInitialFormData(selectedDate, getCurrentTime, getToday));
  const [customerSearchOpen, setCustomerSearchOpen] = useState(false);
  const [parts, setParts] = useState([]);
  const [payments, setPayments] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [dataLoadError, setDataLoadError] = useState(false);
  const [sendAssignmentSMS, setSendAssignmentSMS] = useState(false);
  // Files picked before the job exists — uploaded right after creation.
  const [pendingFiles, setPendingFiles] = useState([]);
  // Fresh tax snapshot returned by JobTaxPanel override/remove actions —
  // overlays editingJob so the Financial Summary updates without a refetch.
  const [taxSnapshot, setTaxSnapshot] = useState(null);
  // Live tax preview from JobTaxPanel — feeds the Job Completion summary
  // so tax + customer total show before the job is saved.
  const [taxPreview, setTaxPreview] = useState(null);
  // Custom tax override drafted on a NEW job — included in the create payload.
  const [taxOverrideDraft, setTaxOverrideDraft] = useState(null);
  React.useEffect(() => { setTaxSnapshot(null); setTaxPreview(null); setTaxOverrideDraft(null); }, [open, editingJob]);
  // Linked customer viewer/editor — opens over the job form, closing returns here
  const [customerModal, setCustomerModal] = useState(null);

  const openLinkedCustomer = async () => {
    if (!formData.customer_id) return;
    const found = (customers || []).find((c) => c.customer_id === formData.customer_id);
    if (found) { setCustomerModal(found); return; }
    try {
      const res = await axios.get(`${API}/customers/${formData.customer_id}`, getAuthConfig());
      setCustomerModal(res.data);
    } catch {
      toast.error("Could not load customer");
    }
  };

  const onLinkedCustomerSaved = (updated) => {
    setCustomerModal(updated);
    setFormData((prev) => prev.customer_id === updated.customer_id ? {
      ...prev,
      customer_name: updated.name ?? prev.customer_name,
      customer_phone: updated.phone ?? prev.customer_phone,
      customer_phone2: updated.secondary_phone ?? prev.customer_phone2,
      customer_email: updated.email ?? prev.customer_email,
    } : prev);
  };

  // A job clicked inside the customer's Jobs tab — jump to that job's edit page.
  const openCustomerJob = (j) => {
    setCustomerModal(null);
    if (editingJob?.job_id === j.job_id) return;
    if (!isPage) onOpenChange?.(false);
    navigate(`/dashboard/jobs/${j.job_id}/edit`);
  };
  // Address picker state — shared `<CustomerAddressPicker>` component is
  // mounted at the bottom of the dialog so single-location and store flows
  // get the same picker UX (auto-skip on single saved address, inline
  // "+ Add New Address" form with Google Places autocomplete, save back
  // to customer profile).
  const [showAddressPicker, setShowAddressPicker] = useState(false);
  const [pickerCustomer, setPickerCustomer] = useState(null);

  // Multi-location picker. Loaded once on open; the field is hidden when
  // the account has < 2 active locations so single-location users see no
  // extra UI. Falls back to the account's default location for new jobs.
  const [storeLocations, setStoreLocations] = useState([]);
  const showLocationPicker = storeLocations.length >= 2;
  React.useEffect(() => {
    if (!open) return;
    axios.get(`${API}/store/locations`, getAuthConfig())
      .then(r => {
        const list = Array.isArray(r.data) ? r.data : (r.data?.locations || []);
        setStoreLocations(list.filter(l => l.active !== false));
      })
      .catch(() => setStoreLocations([]));
  }, [open]);

  // Default the picker to the account's default location for FRESH jobs.
  // Runs once locations arrive AND only when we haven't already set one
  // (so editing-job + ticketPrefill paths win).
  React.useEffect(() => {
    if (!open || editingJob || storeLocations.length === 0) return;
    if (formData.store_location_id) return;
    const def = storeLocations.find(l => l.is_default) || storeLocations[0];
    if (def) setFormData(prev => ({ ...prev, store_location_id: def.location_id }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, storeLocations, editingJob]);

  // Custom hooks
  const address = useAddressAutocomplete(setFormData);
  const { handleSourceSelect, handleTechnicianSelect, handleServiceSelect } = useJobSelections(
    setFormData, jobSources, jobTypes, teamMembers
  );

  const resetForm = useCallback(() => {
    setFormData(getInitialFormData(selectedDate, getCurrentTime));
    setParts([]);
    setPayments([]);
    setSendAssignmentSMS(false);
    setPendingFiles([]);
    address.resetAddress();
  }, [selectedDate, getCurrentTime, address.resetAddress]);

  // Populate form when editing
  React.useEffect(() => {
    if (editingJob && open) {
      // Linked customer fallback: older jobs may have blanked contact fields
      const linkedCust = editingJob.customer_id
        ? (customers || []).find((c) => c.customer_id === editingJob.customer_id)
        : null;
      setFormData({
        source_id: editingJob.source_id || "",
        service_id: editingJob.service_id || "",
        service_type: editingJob.service_type || "other",
        assigned_to: editingJob.assigned_to || "",
        title: editingJob.title || "",
        customer_id: editingJob.customer_id || "",
        customer_name: editingJob.customer_name || linkedCust?.name || "",
        customer_phone: editingJob.customer_phone || linkedCust?.phone || "",
        customer_phone2: editingJob.customer_phone2 || linkedCust?.secondary_phone || "",
        customer_phone_ext: editingJob.customer_phone_ext || "",
        customer_phone2_ext: editingJob.customer_phone2_ext || "",
        customer_email: editingJob.customer_email || linkedCust?.email || "",
        customer_address: editingJob.customer_address || "",
        address_city: editingJob.address_city || "",
        address_state: editingJob.address_state || "",
        zip_code: editingJob.zip_code || "",
        scheduled_date: new Date(editingJob.scheduled_date),
        scheduled_time: editingJob.scheduled_time || "",
        schedule_type: editingJob.schedule_type || (editingJob.scheduled_time_end ? "between" : "exact"),
        scheduled_time_end: editingJob.scheduled_time_end || "",
        latitude: editingJob.latitude ?? null,
        longitude: editingJob.longitude ?? null,
        duration_hours: editingJob.duration_hours || 1,
        notes: editingJob.notes || "",
        status: editingJob.status || "scheduled",
        total_charged: editingJob.total_charged || "",
        source_commission_type: editingJob.source_commission_type || "percentage",
        source_commission_value: editingJob.source_commission_value || "",
        source_parts_deduction: editingJob.source_parts_deduction || "from_total",
        technician_commission_type: editingJob.technician_commission_type || "percentage",
        technician_commission_value: editingJob.technician_commission_value || "",
        vehicle_make: editingJob.vehicle_make || "",
        vehicle_model: editingJob.vehicle_model || "",
        vehicle_year: editingJob.vehicle_year || "",
        additional_technician_id: editingJob.additional_technician_id || "",
        additional_technician_ids: editingJob.additional_technician_ids
          || (editingJob.additional_technician_id ? [editingJob.additional_technician_id] : []),
        tech_commission_split_mode: editingJob.tech_commission_split_mode || "equal",
        tech_commission_split_unit: editingJob.tech_commission_split_unit || "percent",
        tech_commission_splits_map: Object.fromEntries(
          (editingJob.tech_commission_splits || []).filter(s => s.percent != null).map(s => [s.technician_id, s.percent])
        ),
        tech_commission_splits_amount_map: Object.fromEntries(
          (editingJob.tech_commission_splits || []).filter(s => s.amount != null).map(s => [s.technician_id, s.amount])
        ),
        additional_tech_commission_type: editingJob.additional_tech_commission_type || "percentage",
        additional_tech_commission_value: editingJob.additional_tech_commission_value || "50",
        is_recurring: !!editingJob.is_recurring,
        recurring_interval_value: editingJob.recurring_interval_value || 1,
        recurring_interval_unit: editingJob.recurring_interval_unit || "months",
        created_at: editingJob.created_at || "",
        completed_at: editingJob.completed_at || "",
        tax_disabled: editingJob.tax_disabled ?? false,
        // Preserve the job's existing location on edit so the picker
        // shows the right value (and the persisted assignment doesn't
        // get nulled out when saving).
        store_location_id: editingJob.store_location_id || "",
      });
      if (editingJob.source_id) {
        const source = jobSources.find(s => s.source_id === editingJob.source_id);
        if (source) {
          setFormData(prev => ({
            ...prev,
            source_extra_fee_enabled: !!source.extra_fee_enabled,
            source_extra_fee_per_job: source.extra_fee_per_job || 0
          }));
        }
      }
      setParts(editingJob.parts || []);
      const loadedPayments = (editingJob.payments || []).map(p => {
        if (p.collected_by) return p;
        const pt = paymentTypes.find(t => t.payment_id === p.payment_type_id);
        return { ...p, collected_by: pt?.default_collected_by || "company" };
      });
      setPayments(loadedPayments);
    } else if (!open) {
      resetForm();
    }
    // NOTE: resetForm excluded — unstable identity (context churn) used to
    // re-run this effect mid-edit and revert unsaved changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingJob, open]);

  // Populate form from parsed ticket data
  React.useEffect(() => {
    if (ticketPrefill && open && !editingJob) {
      const d = ticketPrefill;
      const partsArr = [];
      if (d.parts_cost && d.parts_cost > 0) {
        partsArr.push({ name: "Parts", cost: d.parts_cost, provider: "company" });
      }
      const noteLines = [];
      if (d.job_reference) noteLines.push(`Ticket Ref: ${d.job_reference}`);
      if (d.source_name && !d.matched_source_id) noteLines.push(`Source: ${d.source_name} (not matched)`);
      if (d.technician_name && !d.matched_technician_id) noteLines.push(`Tech: ${d.technician_name} (not matched)`);
      if (d.job_type && !d.matched_service_id) noteLines.push(`Job Type: ${d.job_type} (not matched)`);
      setFormData(prev => ({
        ...prev,
        source_id: d.matched_source_id || "",
        service_id: d.matched_service_id || "",
        service_type: "other",
        assigned_to: d.matched_technician_id || "",
        title: d.job_type || d.job_reference || "Closing Ticket Job",
        customer_id: "",
        customer_name: d.customer_name || "",
        customer_phone: d.customer_phone || "",
        customer_phone2: d.customer_phone2 || "",
        customer_phone_ext: d.customer_phone_ext || "",
        customer_phone2_ext: d.customer_phone2_ext || "",
        customer_email: "",
        customer_address: d.address_street || d.customer_address || "",
        address_city: d.address_city || "",
        address_state: d.address_state || "",
        zip_code: d.zip_code || "",
        scheduled_date: d.scheduled_date ? new Date(`${d.scheduled_date}T12:00:00`) : getToday(),
        scheduled_time: d.scheduled_time || prev.scheduled_time,
        schedule_type: d.scheduled_time_end ? "between" : "exact",
        scheduled_time_end: d.scheduled_time_end || "",
        status: d.total_amount ? "completed" : (d.default_status || "new-lead"),
        total_charged: d.total_amount || "",
        source_commission_type: d.source_commission_type || "percentage",
        source_commission_value: d.source_commission_value || "",
        source_parts_deduction: d.source_parts_deduction || "from_total",
        technician_commission_type: d.technician_commission_type || "percentage",
        technician_commission_value: d.technician_commission_value || "",
        notes: [d.notes, noteLines.join("\n")].filter(Boolean).join("\n"),
      }));
      setParts(partsArr);
    }
  }, [ticketPrefill, open, editingJob]);

  // Populate ONLY the customer fields (quick "New Job for Customer" action)
  React.useEffect(() => {
    if (customerPrefill && open && !editingJob && !ticketPrefill) {
      const c = customerPrefill;
      setFormData(prev => ({
        ...prev,
        customer_id: c.customer_id || "",
        customer_name: c.customer_name || "",
        customer_phone: c.customer_phone || "",
        customer_email: c.customer_email || "",
        customer_address: c.customer_address || "",
        address_city: c.address_city || "",
        address_state: c.address_state || "",
        zip_code: c.zip_code || "",
      }));
    }
  }, [customerPrefill, open, editingJob, ticketPrefill]);

  // Timeline drag-to-create: seed schedule fields (date/time/duration/tech)
  React.useEffect(() => {
    if (schedulePrefill && open && !editingJob && !ticketPrefill) {
      const s = schedulePrefill;
      setFormData(prev => ({
        ...prev,
        scheduled_date: s.scheduled_date ? new Date(`${s.scheduled_date}T00:00:00`) : prev.scheduled_date,
        scheduled_time: s.scheduled_time || prev.scheduled_time,
        schedule_type: "exact",
        duration_hours: s.duration_hours || prev.duration_hours,
        assigned_to: s.assigned_to || prev.assigned_to,
      }));
    }
  }, [schedulePrefill, open, editingJob, ticketPrefill]);

  // Autofill Total Charged (pre-tax, pre-credit-fee) from the job's linked
  // invoice when the job is being marked completed and no total has been
  // entered yet. Also captures the invoice's credit fee for the breakdown.
  const invAutofillRef = React.useRef(null);
  const [linkedInvFee, setLinkedInvFee] = React.useState(null);
  React.useEffect(() => { invAutofillRef.current = null; setLinkedInvFee(null); }, [open, editingJob]);
  React.useEffect(() => {
    const jobId = editingJob?.job_id;
    const isDone = formData.status === "completed" || formData.status === "completed_pending_payment";
    if (!open || !jobId || !isDone) return;
    if (invAutofillRef.current === jobId) return;
    invAutofillRef.current = jobId;
    const wantAutofill = !(parseFloat(formData.total_charged) > 0);
    const token = localStorage.getItem("token");
    axios.get(`${API}/invoices?job_id=${jobId}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then((res) => {
        const docs = (Array.isArray(res.data) ? res.data : [])
          .filter((d) => d.document_type !== "estimate" && d.status !== "cancelled");
        if (!docs.length) { setLinkedInvFee({ amount: 0 }); return; }
        docs.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
        const inv = docs[0];
        const fee = inv.credit_fee_enabled ? (parseFloat(inv.credit_fee_amount) || 0) : 0;
        setLinkedInvFee({ amount: fee, value: inv.credit_fee_value || 0, type: inv.credit_fee_type || "percentage" });
        const preTax = Math.round(((inv.total || 0) - (inv.tax_amount || 0) - fee) * 100) / 100;
        if (wantAutofill && preTax > 0) {
          setFormData((prev) => (parseFloat(prev.total_charged) > 0 ? prev : { ...prev, total_charged: String(preTax) }));
          toast.info(`Total charged auto-filled from Invoice #${inv.invoice_number} — $${preTax.toFixed(2)} (pre-tax${fee > 0 ? ", excl. credit fee" : ""})`);
        }
      })
      .catch(() => {});
  }, [open, formData.status, formData.total_charged, editingJob?.job_id]);

  const selectCustomer = (customer) => {
    // Legacy records may keep the whole address in one string — split it so
    // every address field autofills (street/city/state/zip).
    const legacy = splitLegacyAddress(customer);
    setFormData(prev => ({
      ...prev,
      customer_id: customer.customer_id,
      customer_name: customer.name,
      customer_phone: customer.phone,
      customer_phone2: customer.secondary_phone || prev.customer_phone2 || "",
      customer_email: customer.email || "",
      customer_address: legacy.street || "",
      address_city: legacy.city || prev.address_city || "",
      address_state: legacy.state || prev.address_state || "",
      zip_code: legacy.zip || prev.zip_code || "",
    }));
    setCustomerSearchOpen(false);
    // Open the unified address picker — same component used by both
    // single-location (this) and multi-location (StoreJobFormDialog) flows.
    // The picker fetches addresses itself, auto-skips on single-address,
    // and offers an inline "+ Add New Address" form with autocomplete.
    setPickerCustomer(customer);
    setShowAddressPicker(true);
  };

  const handlePickedAddress = useCallback((addr) => {
    if (!addr) return;
    // Saved address rows can also be legacy one-line strings — split them so
    // city/state/zip always populate.
    const legacy = splitLegacyAddress({
      address: addr.address_line1, city: addr.city, state: addr.state, zip_code: addr.zip_code,
    });
    setFormData(prev => ({
      ...prev,
      customer_address: legacy.street || "",
      address_city: legacy.city || "",
      address_state: legacy.state || "",
      zip_code: legacy.zip || "",
    }));
  }, []);

  const closeAddressPicker = useCallback(() => setShowAddressPicker(false), []);

  // --- Form Submission ---
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.customer_id) {
      if (!formData.customer_name.trim() || !formData.customer_phone.trim()) {
        toast.error("Customer name and phone are required"); return;
      }
    }

    if (formData.tech_commission_split_mode === "custom" && formData.tech_commission_split_unit !== "amount") {
      const extras = (formData.additional_technician_ids || []).filter(t => t && t.trim());
      if (extras.length > 0) {
        const ids = [formData.assigned_to, ...extras].filter(Boolean);
        const eq = 100 / ids.length;
        const total = ids.reduce((s, id) => {
          const v = parseFloat((formData.tech_commission_splits_map || {})[id]);
          return s + (isNaN(v) ? eq : v);
        }, 0);
        if (Math.abs(total - 100) >= 0.5) {
          toast.error(`Custom commission split must total 100% (currently ${Math.round(total * 100) / 100}%)`);
          return;
        }
      }
    }

    if ((formData.status === "completed" || formData.status === "completed_pending_payment") && payments.length > 0) {
      const totals = calculateTotals(formData, parts, payments, teamMembers, paymentTypes);
      if (Math.abs(totals.paymentsTotal - totals.totalCharged) > 0.01) {
        toast.error(`Payment total ($${totals.paymentsTotal.toFixed(2)}) doesn't match amount charged ($${totals.totalCharged.toFixed(2)})`);
        return;
      }
    }

    setSubmitting(true);
    try {
      const payload = buildJobPayload(formData, {
        customerMode: formData.customer_id ? "existing" : "new",
        parts, payments, teamMembers, paymentTypes, jobTypes, sendAssignmentSMS,
      });
      if (!editingJob && taxOverrideDraft) payload.tax_override = taxOverrideDraft;
      if (!editingJob && ticketPrefill) payload.force_new_customer = true;
      const config = getAuthConfig();
      if (editingJob) {
        await axios.put(`${API}/jobs/${editingJob.job_id}`, payload, config);
        toast.success("Job updated successfully");
      } else {
        const res = await axios.post(`${API}/jobs`, payload, config);
        toast.success("Job created successfully");
        if (pendingFiles.length > 0 && res.data?.job_id) {
          const { uploaded, failed } = await uploadJobAttachments(res.data.job_id, pendingFiles);
          if (uploaded.length) toast.success(`${uploaded.length} attachment${uploaded.length > 1 ? "s" : ""} uploaded`);
          if (failed.length) toast.error(`Failed to upload: ${failed.map((f) => f.file.name).join(", ")} — you can re-upload from the job details`);
        }
      }
      onOpenChange(false);
      resetForm();
      onSuccess?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save job");
    } finally {
      setSubmitting(false);
    }
  };

  const formBody = (
    <form id="job-form" onSubmit={handleSubmit} className={isPage ? "space-y-6" : "p-4 sm:p-6 pt-4 space-y-6 pb-4"}>

            {dataLoadError && (
              <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-red-400 shrink-0" />
                <div className="flex-1">
                  <p className="text-red-400 text-sm font-medium">Failed to load form data</p>
                  <p className="text-muted-foreground text-xs mt-0.5">Check your connection and try refreshing</p>
                </div>
                <Button type="button" size="sm" variant="ghost" onClick={onRefresh} className="text-red-400 hover:text-red-300">
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>
            )}

            {/* Status */}
            <div className="flex items-center justify-between p-3 rounded-xl bg-theme-surface border border-theme">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: getStatus(formData.status)?.color || "#3b82f6" }} />
                <Label className="text-foreground text-sm font-medium">Status</Label>
              </div>
              <Select value={formData.status} onValueChange={(v) => {
                const newData = { ...formData, status: v };
                if ((v === "completed" || v === "completed_pending_payment" || getStatus(v)?.category === "completed") && formData.assigned_to && !parseFloat(formData.technician_commission_value)) {
                  const tech = teamMembers.find(m => m.member_id === formData.assigned_to);
                  const comm = getTechCommission(tech, formData.source_id, formData.service_id);
                  if (comm.value) { newData.technician_commission_type = comm.type; newData.technician_commission_value = comm.value; }
                }
                setFormData(newData);
              }}>
                <SelectTrigger className="w-40 input-glass" data-testid="job-status-select-top">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(statusOptions.length ? statusOptions : FALLBACK_STATUS_OPTIONS).map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>
                  ))}
                  {formData.status && statusOptions.length > 0 && !statusOptions.some((s) => s.id === formData.status) && (
                    <SelectItem value={formData.status}>{getStatus(formData.status)?.label || formData.status} (archived)</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* Location picker — only renders for accounts with ≥2 active
                store locations. Saves the picked location_id onto the job
                via `store_location_id` so per-location filtering on Jobs,
                Financials, and per-location senders all work. */}
            {showLocationPicker && (
              <div className="flex items-center justify-between p-3 rounded-xl bg-theme-surface border border-theme">
                <div className="flex items-center gap-2">
                  <Label className="text-foreground text-sm font-medium">Location</Label>
                </div>
                <Select
                  value={formData.store_location_id || "__none"}
                  onValueChange={(v) => setFormData({ ...formData, store_location_id: v === "__none" ? "" : v })}
                >
                  <SelectTrigger className="w-48 input-glass" data-testid="job-location-select">
                    <SelectValue placeholder="Select location" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none">No specific location</SelectItem>
                    {storeLocations.map(loc => (
                      <SelectItem key={loc.location_id} value={loc.location_id}>
                        {loc.name}{loc.industry ? ` — ${loc.industry}` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <JobDetailsSection
              formData={formData} setFormData={setFormData} loading={loading}
              jobSources={jobSources} jobTypes={jobTypes}
              teamMembers={formData.store_location_id
                ? teamMembers.filter(t => !t.location_ids || t.location_ids.length === 0 || t.location_ids.includes(formData.store_location_id) || t.member_id === formData.assigned_to)
                : teamMembers}
              handleSourceSelect={handleSourceSelect} handleServiceSelect={handleServiceSelect} handleTechnicianSelect={handleTechnicianSelect}
              sendAssignmentSMS={sendAssignmentSMS} setSendAssignmentSMS={setSendAssignmentSMS}
              isSmsReady={isSmsReady} hasActivePhone={hasActivePhone} editingJob={editingJob}
            />

            {isAutomotiveJob(jobTypes, formData.service_id) && (
              <VehicleSection formData={formData} setFormData={setFormData} vehicleData={vehicleData} />
            )}

            <Separator className="bg-theme-hover" />

            <CustomerSection
              formData={formData} setFormData={setFormData}
              customers={customers} selectCustomer={selectCustomer}
              onOpenCustomer={openLinkedCustomer}
            />

            <Separator className="bg-theme-hover" />

            <AddressSection
              formData={formData} setFormData={setFormData}
              addressSuggestions={address.addressSuggestions} showAddressSuggestions={address.showAddressSuggestions} addressLoading={address.addressLoading}
              handleAddressChange={address.handleAddressChange} selectAddress={address.selectAddress} setShowAddressSuggestions={address.setShowAddressSuggestions}
            />

            <Separator className="bg-theme-hover" />

            <ScheduleSection formData={formData} setFormData={setFormData} getCurrentTime={getCurrentTime} />

            {/* Dates (collapsible — directly below Schedule) */}
            <JobDatesSection
              createdAt={formData.created_at}
              completedAt={formData.completed_at}
              scheduledDate={formData.scheduled_date instanceof Date
                ? formData.scheduled_date.toLocaleDateString()
                : (formData.scheduled_date || "")}
              isCompleted={["completed", "done", "completed_pending_payment"].includes((formData.status || "").toLowerCase())}
              onChange={({ created_at, completed_at }) => {
                setFormData((prev) => ({
                  ...prev,
                  ...(created_at !== undefined ? { created_at } : {}),
                  ...(completed_at !== undefined ? { completed_at } : {}),
                }));
              }}
            />

            <Separator className="bg-theme-hover" />

            <div className="space-y-4">
              <div className="flex items-center gap-2 text-[#FFCC00]">
                <Briefcase className="w-4 h-4" />
                <span className="text-sm font-medium">Additional Notes</span>
              </div>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                className="input-glass min-h-[140px] resize-y"
                placeholder="Special instructions, access codes, notes..."
                data-testid="job-notes-textarea"
              />
            </div>

            <Separator className="bg-theme-hover" />

            {/* Attachments — live list on edit, deferred picker on create */}
            {editingJob?.job_id ? (
              <JobAttachments jobId={editingJob.job_id} />
            ) : (
              <PendingAttachmentsPicker files={pendingFiles} onChange={setPendingFiles} />
            )}

            {(formData.status === "completed" || formData.status === "completed_pending_payment") && (
              <CompletionSection
                formData={formData} setFormData={setFormData}
                parts={parts} setParts={setParts} payments={payments} setPayments={setPayments}
                teamMembers={teamMembers} paymentTypes={paymentTypes}
                editingJob={editingJob ? { ...editingJob, ...(taxSnapshot || {}) } : editingJob}
                liveTax={taxPreview}
                linkedInvoiceFee={linkedInvFee}
              />
            )}

            {/* ── Recurring job (renders only when enabled in Business Settings) ── */}
            <RecurringJobPanel
              value={formData}
              onChange={(patch) => setFormData((prev) => ({ ...prev, ...patch }))}
            />

            {/* ── Automatic tax (renders only when the business enabled it) ── */}
            <JobTaxPanel
              jobId={editingJob?.job_id}
              address={formData.customer_address}
              city={formData.address_city}
              state={formData.address_state}
              zip={formData.zip_code}
              amount={formData.total_charged}
              customerId={formData.customer_id}
              locationId={formData.store_location_id}
              savedJob={editingJob ? { ...editingJob, ...(taxSnapshot || {}) } : editingJob}
              onTaxChange={setTaxSnapshot}
              onPreviewChange={setTaxPreview}
              taxDisabled={!!formData.tax_disabled}
              onTaxDisabledChange={(v) => setFormData(prev => ({ ...prev, tax_disabled: v }))}
              onOverrideDraftChange={setTaxOverrideDraft}
            />
    </form>
  );

  const footerButtons = (
    <>
      <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} className="text-muted-foreground">
        Cancel
      </Button>
      <Button type="submit" form="job-form" className="btn-primary" disabled={submitting} data-testid="save-job-btn">
        {submitting ? "Saving..." : (editingJob ? "Update Job" : "Create Job")}
      </Button>
    </>
  );

  const addressPicker = (
    <CustomerAddressPicker
      customer={pickerCustomer}
      open={showAddressPicker}
      onClose={closeAddressPicker}
      onPick={handlePickedAddress}
    />
  );

  const linkedCustomerModal = (
    <CustomerModal
      open={!!customerModal}
      onOpenChange={(o) => { if (!o) setCustomerModal(null); }}
      customer={customerModal}
      onSaved={onLinkedCustomerSaved}
      onOpenJob={openCustomerJob}
      elevated
    />
  );

  // ── Full-page variant — no Dialog wrapper, so nested dropdowns can't
  //    fight the modal focus trap. Used by /dashboard/jobs/new + /edit. ──
  if (isPage) {
    return (
      <div data-testid="job-form-page-body">
        {formBody}
        <div className="sticky bottom-0 z-10 mt-6 flex justify-end gap-3 py-4 border-t border-border bg-background">
          {footerButtons}
        </div>
        {addressPicker}
        {linkedCustomerModal}
      </div>
    );
  }

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { onOpenChange(isOpen); if (!isOpen) resetForm(); }}>
      <DialogTrigger asChild>
        <Button className="btn-primary whitespace-nowrap px-2.5 sm:px-3" size="sm" data-testid="add-job-btn">
          <Plus className="w-4 h-4 sm:mr-1.5 shrink-0" />
          <span className="hidden sm:inline">New Job</span>
        </Button>
      </DialogTrigger>
      <DialogContent
        className="bg-background border-border p-0 flex flex-col overflow-hidden !z-[60] !inset-0 !translate-x-0 !translate-y-0 !max-w-none !max-h-none !rounded-none !border-0 sm:!inset-auto sm:!left-[50%] sm:!top-[50%] sm:!translate-x-[-50%] sm:!translate-y-[-50%] sm:!w-[95vw] sm:!h-auto sm:!max-w-2xl sm:!max-h-[90vh] sm:!rounded-lg sm:!border"
        {...dialogContentOutsideGuards}
      >
        <DialogHeader className="p-4 sm:p-6 pb-0 flex-shrink-0">
          <div className="flex items-center gap-2 pr-8">
            <DialogTitle className="text-foreground font-['Manrope']">
              {editingJob ? "Edit Job" : "Create New Job"}
            </DialogTitle>
            <Button
              type="button" variant="ghost" size="icon"
              onClick={() => { onRefresh?.(); toast.info("Refreshing data..."); }}
              className="h-7 w-7 text-muted-foreground hover:text-foreground"
              data-testid="refresh-data-btn"
            >
              <RefreshCw className="w-3.5 h-3.5" />
            </Button>
          </div>
        </DialogHeader>

        <ScrollArea className="flex-1 overflow-auto">
          {formBody}
        </ScrollArea>

        <div className="flex-shrink-0 flex justify-end gap-3 p-4 border-t border-border bg-background">
          {footerButtons}
        </div>
      </DialogContent>
      {addressPicker}
      {linkedCustomerModal}
    </Dialog>
  );
};

export default JobFormDialog;
